/**
 * @file LocaleProvider.tsx
 *
 * Holds the active admin-UI locale and feeds react-intl. The locale is
 * switchable at runtime (no reload): changing it lazy-loads that catalog,
 * re-renders the IntlProvider, persists the choice to localStorage, and updates
 * `<html lang>`. The initial locale comes from the `locale` URL param (Website
 * Builder sets it on the iframe in token mode), then the stored choice, then the
 * browser language, resolved against the supported set. The context and hook
 * live in {@link ./locale-context}.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { DEFAULT_LOCALE, resolveLocale, type SupportedLocale } from '@wsb/i18n';
import { loadMessages } from './load-messages';
import {
  ADMIN_LOCALE_STORAGE_KEY,
  LocaleContext,
  type LocaleContextValue,
} from './locale-context';

const LOCALE_QUERY_PARAM = 'locale';

function initialLocale(): SupportedLocale {
  const fromUrl = new URLSearchParams(window.location.search).get(
    LOCALE_QUERY_PARAM,
  );
  const stored = localStorage.getItem(ADMIN_LOCALE_STORAGE_KEY);
  return resolveLocale(fromUrl ?? stored ?? navigator.language);
}

/**
 * Provides the admin-UI locale context and wraps children in a react-intl
 * `IntlProvider` whose messages are lazy-loaded for the active locale.
 *
 * @param props.children - The subtree that consumes translations.
 *
 * @returns The provider element.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale);
  const [messages, setMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    loadMessages(locale).then((loaded) => {
      if (active) setMessages(loaded);
    });
    document.documentElement.lang = locale;
    return () => {
      active = false;
    };
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (next) => {
        localStorage.setItem(ADMIN_LOCALE_STORAGE_KEY, next);
        setLocaleState(next);
      },
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
